import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaFileVideo,
    FaFilePdf,
    FaBookOpen,
    FaCheckCircle,
    FaCloudUploadAlt,
    FaHandPointer,
    FaTimes,
    FaChevronRight
} from 'react-icons/fa';
import DataTable from '../../SuperAdmin/shared/DataTable';
import Loader from '../../shared/Loader';
import { showSuccess, showError, showLoading, closeLoading } from '../../../utils/Alert';
import './TutorDashboardOverview.css';
import '../../SuperAdmin/views/UserModals.css';

const TutorLMS = () => {
    const [loading, setLoading] = useState(true);

    // Core Data States
    const [courses, setCourses] = useState([]);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [selectedModule, setSelectedModule] = useState(null);

    // Lessons currently scoped to the selected Module
    const [lessons, setLessons] = useState([]);

    const [isTopicModalOpen, setIsTopicModalOpen] = useState(false);
    const [editingLessonId, setEditingLessonId] = useState(null);

    // Dynamic Form State for Lessons
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        contentType: 'Video',
        sequence: '',
        isRequired: true,
        mediaFile: null,
        duration: '', // for video/doc
        passingPercentage: 70, // for quiz
        questions: [{ questionText: '', options: ['', '', '', ''], correctAnswer: '', marks: 5 }], // for quiz
        instructions: '', // for assignment
        maxMarks: 100, // for assignment
        dueDate: '' // for assignment
    });

    useEffect(() => {
        fetchTutorCourses();
    }, []);

    const fetchTutorCourses = async () => {
        try {
            const res = await axios.get('/api/tutor/courses', {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            // Only show courses that are Active or Draft (maybe pending too, but active is focus)
            const availableCourses = res.data.filter(c => c.status === 'Active' || c.status === 'Pending');
            setCourses(availableCourses);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching courses for LMS", err);
            showError("Failed to Load", "Could not fetch your courses.");
            setLoading(false);
        }
    };

    const handleCourseSelect = (e) => {
        const courseId = e.target.value;
        if (!courseId) {
            setSelectedCourse(null);
            setSelectedModule(null);
            setLessons([]);
            return;
        }
        const course = courses.find(c => c._id === courseId);
        setSelectedCourse(course);
        setSelectedModule(null); // Reset module when course changes
        setLessons([]);
    };

    const handleModuleSelect = async (e) => {
        const moduleId = e.target.value;
        if (!moduleId) {
            setSelectedModule(null);
            setLessons([]);
            return;
        }

        const mod = selectedCourse.modules.find(m => m._id === moduleId);
        setSelectedModule(mod);

        // Fetch Lessons for this module
        await fetchLessonsForModule(selectedCourse._id, moduleId);
    };

    const fetchLessonsForModule = async (courseId, moduleId) => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/tutor/courses/${courseId}/modules/${moduleId}/lessons`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setLessons(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching lessons", err);
            showError("Error", "Failed to load content for this module.");
            setLoading(false);
        }
    };

    const handleDeleteLesson = async (lessonId) => {
        if (!window.confirm("Are you sure you want to permanently delete this content?")) return;

        showLoading("Removing Content...");
        try {
            await axios.delete(`/api/tutor/courses/${selectedCourse._id}/modules/${selectedModule._id}/lessons/${lessonId}`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            showSuccess("Deleted", "Lesson removed from curriculum.");
            fetchLessonsForModule(selectedCourse._id, selectedModule._id);
        } catch (err) {
            showError("Delete Failed", "Could not remove lesson.");
            closeLoading();
        }
    };

    const handleFormChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({ ...prev, mediaFile: e.target.files[0] }));
    };

    const handleQuestionChange = (index, field, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[index][field] = value;
        setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    };

    const handleOptionChange = (qIndex, oIndex, value) => {
        const updatedQuestions = [...formData.questions];
        updatedQuestions[qIndex].options[oIndex] = value;
        setFormData(prev => ({ ...prev, questions: updatedQuestions }));
    };

    const addQuestion = () => {
        setFormData(prev => ({
            ...prev,
            questions: [...prev.questions, { questionText: '', options: ['', '', '', ''], correctAnswer: '', marks: 5 }]
        }));
    };

    const removeQuestion = (index) => {
        setFormData(prev => ({
            ...prev,
            questions: prev.questions.filter((_, i) => i !== index)
        }));
    };

    const handleLessonSubmit = async (e) => {
        e.preventDefault();
        showLoading("Uploading Content...");

        const data = new FormData();
        data.append('title', formData.title);
        data.append('description', formData.description);
        data.append('contentType', formData.contentType);
        data.append('sequence', formData.sequence);
        data.append('isRequired', formData.isRequired);

        if (formData.contentType === 'Video' || formData.contentType === 'Document') {
            if (formData.mediaFile) data.append('mediaFile', formData.mediaFile);
            data.append('duration', formData.duration);
        }

        if (formData.contentType === 'Quiz') {
            data.append('passingPercentage', formData.passingPercentage);
            data.append('questions', JSON.stringify(formData.questions));
        }

        if (formData.contentType === 'Assignment') {
            data.append('instructions', formData.instructions);
            data.append('maxMarks', formData.maxMarks);
            data.append('dueDate', formData.dueDate);
        }

        try {
            const url = editingLessonId
                ? `/api/tutor/courses/${selectedCourse._id}/modules/${selectedModule._id}/lessons/${editingLessonId}`
                : `/api/tutor/courses/${selectedCourse._id}/modules/${selectedModule._id}/lessons`;

            const method = editingLessonId ? 'put' : 'post';

            await axios[method](url, data, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            showSuccess("Success", `Lesson ${editingLessonId ? 'updated' : 'added'} successfully.`);
            setIsTopicModalOpen(false);
            setEditingLessonId(null);
            fetchLessonsForModule(selectedCourse._id, selectedModule._id);
        } catch (err) {
            console.error("Lesson error", err);
            showError("Upload Failed", err.response?.data?.message || "Check your formatting.");
            closeLoading();
        }
    };

    const openCreateModal = () => {
        setEditingLessonId(null);
        setFormData({
            title: '',
            description: '',
            contentType: 'Video',
            sequence: lessons.length + 1,
            isRequired: true,
            mediaFile: null,
            duration: '',
            passingPercentage: 70,
            questions: [{ questionText: '', options: ['', '', '', ''], correctAnswer: '', marks: 5 }],
            instructions: '',
            maxMarks: 100,
            dueDate: ''
        });
        setIsTopicModalOpen(true);
    };

    const openEditModal = (lesson) => {
        setEditingLessonId(lesson._id);
        setFormData({
            ...lesson,
            mediaFile: null, // Don't reload binary
            dueDate: lesson.dueDate ? new Date(lesson.dueDate).toISOString().split('T')[0] : ''
        });
        setIsTopicModalOpen(true);
    };

    const lessonColumns = [
        { header: 'Sequence', key: 'sequence', width: '10%' },
        { header: 'Lesson Title', key: 'title', width: '30%' },
        {
            header: 'Content Type',
            key: 'contentType',
            width: '20%',
            render: (row) => {
                let Icon = FaBookOpen;
                let color = '#64748B';
                if (row.contentType === 'Video') { Icon = FaFileVideo; color = '#E11D48'; }
                if (row.contentType === 'Document') { Icon = FaFilePdf; color = '#2563EB'; }

                return (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontWeight: 500 }}>
                        <Icon style={{ color }} /> {row.contentType}
                    </span>
                );
            }
        },
        {
            header: 'Required',
            key: 'isRequired',
            width: '15%',
            render: (row) => row.isRequired ? <span style={{ color: '#10B981' }}>Yes</span> : <span style={{ color: '#94A3B8' }}>No</span>
        },
        {
            header: 'Actions',
            key: 'actions',
            width: '25%',
            render: (row) => (
                <div className="action-buttons">
                    <button className="action-btn edit" title="Edit Content" onClick={() => openEditModal(row)}>
                        <FaEdit style={{ color: '#F59E0B' }} />
                    </button>
                    <button className="action-btn delete" title="Delete Content" onClick={() => handleDeleteLesson(row._id)}>
                        <FaTrash />
                    </button>
                </div>
            )
        }
    ];

    if (loading && courses.length === 0) return <Loader />;

    return (
        <div className="dashboard-overview fade-in">
            <div className="dashboard-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h1 className="page-title">LMS Content Builder</h1>
                    <p className="page-subtitle">Select a Course and Module to begin uploading and arranging lessons.</p>
                </div>
            </div>

            {/* Selection Engine */}
            <div className="glass-card" style={{ padding: '24px', marginBottom: '30px', display: 'flex', gap: '24px', alignItems: 'center', background: 'rgba(255, 255, 255, 0.7)', border: '1px solid rgba(255, 255, 255, 0.5)' }}>

                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#0F172A', marginBottom: '8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                        1. Select Course Scope
                    </label>
                    <select
                        className="glass-input premium-select"
                        onChange={handleCourseSelect}
                        value={selectedCourse?._id || ''}
                        style={{ background: '#fff', border: '1.5px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', transition: 'none' }}
                    >
                        <option value="">-- Choose a Course --</option>
                        {courses.map(c => (
                            <option key={c._id} value={c._id}>{c.title} ({c.status})</option>
                        ))}
                    </select>
                </div>

                <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', fontSize: '13px', color: '#0F172A', marginBottom: '8px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                        2. Select Module/Chapter
                    </label>
                    <select
                        className="glass-input premium-select"
                        onChange={handleModuleSelect}
                        value={selectedModule?._id || ''}
                        disabled={!selectedCourse || selectedCourse.modules.length === 0}
                        style={{ background: '#fff', border: '1.5px solid #E2E8F0', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', transition: 'none' }}
                    >
                        <option value="">-- Choose Curriculum Module --</option>
                        {selectedCourse && selectedCourse.modules.map((m, index) => (
                            <option key={m._id} value={m._id}>Module {index + 1}: {m.moduleTitle}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content Viewer */}
            {!selectedCourse && (
                <div className="hero-banner glass-card fade-in" style={{
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(248,250,252,0.8) 100%)',
                    border: '1px dashed #CBD5E1',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '400px',
                    borderRadius: '24px',
                    gap: '20px',
                    textAlign: 'center',
                    padding: '40px'
                }}>
                    <div style={{
                        width: '100px',
                        height: '100px',
                        background: 'white',
                        borderRadius: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 10px 25px -5px rgba(0, 44, 95, 0.1)',
                        fontSize: '48px',
                        color: 'var(--primary-blue)'
                    }}>
                        <FaBookOpen />
                    </div>
                    <div>
                        <h2 style={{ color: 'var(--primary-blue)', margin: '0 0 10px 0', fontSize: '24px' }}>Architect Your Curriculum</h2>
                        <p style={{ color: '#64748B', maxWidth: '450px', lineHeight: '1.6', fontSize: '15px' }}>
                            Ready to build an amazing learning experience? Start by selecting one of your courses from the dropdown above to manage its modules and lessons.
                        </p>
                    </div>
                </div>
            )}

            {selectedCourse && !selectedModule && (
                <div className="hero-banner glass-card fade-in" style={{
                    background: 'white',
                    border: '1px solid var(--border-color)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '80px 40px',
                    borderRadius: '24px',
                    textAlign: 'center'
                }}>
                    <div className="pulse-on-hover" style={{
                        fontSize: '56px',
                        color: 'var(--primary-gold)',
                        marginBottom: '24px',
                        filter: 'drop-shadow(0 4px 6px rgba(212, 175, 55, 0.2))'
                    }}>
                        <FaHandPointer style={{ animation: 'bounce 2s infinite' }} />
                    </div>
                    <h3 style={{ color: 'var(--primary-blue)', margin: '0 0 12px 0', fontSize: '22px' }}>Excellent Choice!</h3>
                    <p style={{ color: '#64748B', maxWidth: '500px', lineHeight: '1.6' }}>
                        You're managing <strong>"{selectedCourse.title}"</strong>. <br />
                        Now, pick a specific <strong>Module or Chapter</strong> from the second dropdown to populate your curriculum with videos, quizzes, and digital assets.
                    </p>
                    <div style={{ marginTop: '24px', display: 'flex', gap: '10px' }}>
                        <span className="status-badge active">Course: {selectedCourse.status}</span>
                        <span className="status-badge warning">{selectedCourse.modules.length} Modules Found</span>
                    </div>
                </div>
            )}

            {selectedCourse && selectedModule && (
                <div className="fade-in">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '20px' }}>
                        <div>
                            <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', color: 'var(--text-primary)' }}>
                                {selectedModule.moduleTitle}
                            </h2>
                            <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px' }}>
                                Manage learning materials for this specific chapter.
                            </p>
                        </div>
                        <button className="primary-btn pulse-on-hover" onClick={openCreateModal}>
                            <FaPlus style={{ marginRight: '8px' }} /> Add Content
                        </button>
                    </div>

                    {loading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}><Loader /> <p>Loading Content...</p></div>
                    ) : (
                        <DataTable
                            title={`Lessons (${lessons.length})`}
                            columns={lessonColumns}
                            data={lessons}
                            filterOptions={[]} // No filters needed for inside a specific module really
                        />
                    )}
                </div>
            )}

            {/* LESSON CONTENT MODAL */}
            {isTopicModalOpen && createPortal(
                <div className={`modal-overlay ${isTopicModalOpen ? 'open' : ''}`} onClick={() => setIsTopicModalOpen(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                        <div className="modal-header">
                            <h2>
                                {editingLessonId ? <FaEdit /> : <FaPlus />}
                                {editingLessonId ? 'Update Lesson Content' : 'Architect New Content'}
                            </h2>
                            <button className="modal-close-btn" onClick={() => setIsTopicModalOpen(false)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleLessonSubmit} className="modal-form">
                                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                    <label className="premium-label">Topic Title</label>
                                    <input
                                        type="text" name="title" value={formData.title} onChange={handleFormChange}
                                        className="glass-input" required placeholder="e.g. Introduction to Virtual DOM"
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Content Type</label>
                                    <select name="contentType" value={formData.contentType} onChange={handleFormChange} className="glass-input">
                                        <option value="Video">Video Lecture</option>
                                        <option value="Document">PDF / Document</option>
                                        <option value="Quiz">Interactive Quiz</option>
                                        <option value="Assignment">Practical Assignment</option>
                                    </select>
                                </div>

                                <div className="form-group">
                                    <label className="premium-label">Sequence Order</label>
                                    <input type="number" name="sequence" value={formData.sequence} onChange={handleFormChange} className="glass-input" required />
                                </div>

                                <div className="form-group full-width" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px', background: '#F8FAFC', padding: '12px', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                                    <input type="checkbox" name="isRequired" checked={formData.isRequired} onChange={handleFormChange} id="reqCheck" style={{ width: '18px', height: '18px' }} />
                                    <label className="premium-label" htmlFor="reqCheck" style={{ marginBottom: 0, cursor: 'pointer' }}>Mark as Mandatory Lesson for Completion</label>
                                </div>

                                <div className="form-group full-width">
                                    <label className="premium-label">Brief Description</label>
                                    <textarea name="description" value={formData.description} onChange={handleFormChange} className="glass-input resize-none" rows="2" placeholder="What is this lesson about?" />
                                </div>

                                {/* Dynamic Fields based on Content Type */}

                                {(formData.contentType === 'Video' || formData.contentType === 'Document') && (
                                    <div className="form-group full-width fade-in" style={{ padding: '24px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', marginTop: '10px' }}>
                                        <h4 style={{ margin: '0 0 15px 0', color: 'var(--primary-blue)', fontSize: '15px' }}>
                                            {formData.contentType} Upload Settings
                                        </h4>
                                        <div className="form-group">
                                            <label className="premium-label">Select {formData.contentType} File</label>
                                            <input type="file" onChange={handleFileChange} className="glass-input" accept={formData.contentType === 'Video' ? 'video/*' : '.pdf,.doc,.docx'} style={{ background: 'white' }} />
                                            <small style={{ color: '#64748B' }}>Max size: 500MB. Secure SSL Upload.</small>
                                        </div>
                                        <div className="form-group" style={{ marginTop: '15px' }}>
                                            <label className="premium-label">Estimated Duration (Minutes)</label>
                                            <input type="number" name="duration" value={formData.duration} onChange={handleFormChange} className="glass-input" placeholder="e.g. 15" style={{ background: 'white' }} />
                                        </div>
                                    </div>
                                )}

                                {formData.contentType === 'Quiz' && (
                                    <div className="form-group full-width fade-in" style={{ padding: '24px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', marginTop: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                            <h4 style={{ margin: 0, color: 'var(--primary-blue)', fontSize: '15px' }}>Quiz / Assessment Builder</h4>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <label className="premium-label" style={{ fontSize: '12px' }}>Pass %</label>
                                                <input type="number" name="passingPercentage" value={formData.passingPercentage} onChange={handleFormChange} className="glass-input" style={{ width: '70px', padding: '5px', background: 'white' }} />
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                            {formData.questions.map((q, qIndex) => (
                                                <div key={qIndex} style={{ padding: '20px', background: 'white', borderRadius: '14px', border: '1px solid #CBD5E1', position: 'relative', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' }}>
                                                    <button type="button" onClick={() => removeQuestion(qIndex)} style={{ position: 'absolute', top: '15px', right: '15px', color: '#EF4444', background: '#FEE2E2', border: 'none', borderRadius: '8px', width: '30px', height: '30px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', boxShadow: '0 2px 4px rgba(239, 68, 68, 0.1)' }} title="Remove Question"><FaTrash size={14} /></button>

                                                    <div className="form-group" style={{ marginBottom: '15px' }}>
                                                        <label className="premium-label">Question {qIndex + 1}</label>
                                                        <input
                                                            type="text" value={q.questionText} onChange={(e) => handleQuestionChange(qIndex, 'questionText', e.target.value)}
                                                            className="glass-input" placeholder="Enter question text..." required
                                                        />
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '15px' }}>
                                                        {q.options.map((opt, oIndex) => (
                                                            <div key={oIndex}>
                                                                <input
                                                                    type="text" value={opt} onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                                                                    className="glass-input" placeholder={`Option ${oIndex + 1}`} required
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>

                                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                                        <div className="form-group">
                                                            <label className="premium-label">Correct Answer Key</label>
                                                            <select
                                                                value={q.correctAnswer} onChange={(e) => handleQuestionChange(qIndex, 'correctAnswer', e.target.value)}
                                                                className="glass-input" required
                                                                style={{ borderLeft: '4px solid #10B981' }}
                                                            >
                                                                <option value="">Select Option...</option>
                                                                {q.options.map((optionValue, oIdx) => (
                                                                    <option key={oIdx} value={optionValue}>Option {String.fromCharCode(65 + oIdx)}</option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                        <div className="form-group">
                                                            <label className="premium-label">Marks</label>
                                                            <input
                                                                type="number" value={q.marks} onChange={(e) => handleQuestionChange(qIndex, 'marks', e.target.value)}
                                                                className="glass-input" required
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                            <button type="button" className="action-btn-gold" onClick={addQuestion} style={{ fontSize: '12px', width: 'fit-content' }}>+ Add Question Layer</button>
                                        </div>
                                    </div>
                                )}

                                {formData.contentType === 'Assignment' && (
                                    <div className="form-group full-width fade-in" style={{ padding: '24px', background: '#F8FAFC', borderRadius: '16px', border: '1px solid #E2E8F0', marginTop: '10px' }}>
                                        <h4 style={{ margin: '0 0 15px 0', color: 'var(--primary-blue)', fontSize: '15px' }}>Practical Assignment Setup</h4>
                                        <div className="form-group">
                                            <label className="premium-label">Submission Instructions</label>
                                            <textarea
                                                name="instructions" value={formData.instructions} onChange={handleFormChange}
                                                className="glass-input resize-none" rows="4" placeholder="Explain the project/assignment requirements..."
                                                style={{ background: 'white' }}
                                            />
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '15px' }}>
                                            <div className="form-group">
                                                <label className="premium-label">Max Marks</label>
                                                <input type="number" name="maxMarks" value={formData.maxMarks} onChange={handleFormChange} className="glass-input" style={{ background: 'white' }} />
                                            </div>
                                            <div className="form-group">
                                                <label className="premium-label">Due Date</label>
                                                <input type="date" name="dueDate" value={formData.dueDate} onChange={handleFormChange} className="glass-input" style={{ background: 'white' }} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="modal-actions">
                                    <button type="button" className="action-btn-secondary" onClick={() => setIsTopicModalOpen(false)}>Cancel</button>
                                    <button type="submit" className="action-btn-gold pulse-on-hover">
                                        {editingLessonId ? 'Commit Changes' : 'Deploy to Module'} <FaChevronRight style={{ marginLeft: '10px' }} />
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default TutorLMS;
